import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import type {RootState} from '../redux/store';
import {getPromoList} from '../api/checkout.api';
// Define a type for the slice state

type PromoList = {
  discountAmount: number;
  promoDesc: string;
  promoID: string;
};

type ItemList = {
  itemIdentifier: string;
  discountAmount: number;
  extSellPrice: number;
  itemID: string;
  promoList: PromoList[] | [];
  quantity: number;
  sellPrice: number;
};

type ProductObj = {
  itemDesc: string;
  itemDept: string;
  itemBrand: string;
  itemColour: string;
  itemSize: string;
};

type PromoResponse = {
  storeID: string;
  orderID: string;
  itemList: ItemList[];
  productList: {[key: string]: ProductObj};
};
interface CheckoutState {
  showGobackModal: boolean;
  loading: boolean;
  promoResponse: PromoResponse | null;
  error: string;
  carryBagTotal: number;
  freebiesTotal: number;
  isFreebieAdded: boolean | null;
}

// Define the initial state using that type
const initialState: CheckoutState = {
  showGobackModal: false,
  loading: false,
  promoResponse: null,
  error: '',
  carryBagTotal: 0,
  freebiesTotal: 0,
  isFreebieAdded: null,
};

export const fetchPromoList = createAsyncThunk(
  'checkout/promoList',
  async (parameters: any, thunkApi) => {
    return getPromoList(parameters)
      .then(response => {
        return response;
      })
      .catch(error => {
        return thunkApi.rejectWithValue(error.response.data);
      });
  },
);

export const checkoutSlice = createSlice({
  name: 'checkoutSlice',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setShowGobackModal: (state, action: PayloadAction<boolean>) => {
      state.showGobackModal = action.payload;
    },
    setCarryBagTotal: (state, action: PayloadAction<number>) => {
      state.carryBagTotal = action.payload;
    },
    setAddedFreebies: (state, action: PayloadAction<number>) => {
      state.freebiesTotal = action.payload;
      state.isFreebieAdded = true;
    },
    setIsFreebiesAdded: (state, action: PayloadAction<boolean>) => {
      state.isFreebieAdded = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchPromoList.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchPromoList.fulfilled, (state, {payload}) => {
      state.promoResponse = payload;
      state.loading = false;
    });
    builder.addCase(fetchPromoList.rejected, (state, action) => {
      state.error = action.toString();
      state.loading = false;
    });
  },
});

export const {
  setShowGobackModal,
  setCarryBagTotal,
  setAddedFreebies,
  setIsFreebiesAdded,
} = checkoutSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const showGobackModal = (state: RootState): boolean =>
  state.checkoutReducer.showGobackModal;
export const promoResponse = (state: RootState): PromoResponse | null =>
  state.checkoutReducer.promoResponse;
export const carryBagTotal = (state: RootState): number =>
  state.checkoutReducer.carryBagTotal;
export const freebiesTotal = (state: RootState): number =>
  state.checkoutReducer.freebiesTotal;
export const isFreebieAdded = (state: RootState): boolean | null =>
  state.checkoutReducer.isFreebieAdded;

export default checkoutSlice.reducer;
